/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect} from "react";
import clsx from "clsx";
import {CheckIcon} from "@heroicons/react/20/solid";
import {PackagesCartItem} from "types/entities/PackagesCartItem";
import {toast} from "react-toastify";
import {Reservation} from "types/entities/Reservation";
import {useTranslation} from "react-i18next";
import {useDispatch} from "useDispatch";
import {useDebouncedCallback} from "use-debounce";
import SellerAction from "store/seller/SellerAction";

interface BookingSellerReservationDetailUpsellQuantityProps {
    packagesCartItem: PackagesCartItem;
    reservation: Reservation;
}

const BookingSellerReservationDetailUpsellQuantity = (props: BookingSellerReservationDetailUpsellQuantityProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const minCount = 1;
    const maxCount = 5000;
    const [upsellCount, setUpsellCount] = React.useState<number>(1);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const inputButton = "border-orange-400 bg-orange-400 text-white flex items-center justify-center w-8 h-[30px] font-bold text-xl leading-tight py-1";
    const isNotUpdatedValue = props.packagesCartItem.drivers_count_adult + props.packagesCartItem.drivers_count_child != upsellCount;

    const onCountHandler = (state: number) => {
        const decreaseCount = upsellCount - 1;
        const increaseCount = upsellCount + 1;
        const newCount = state === -1 ? (decreaseCount < minCount ? minCount : decreaseCount) : increaseCount > maxCount ? maxCount : increaseCount;
        setUpsellCount(newCount);
    };

    const onChangeCountHandler = (newValue: string) => {
        const numberValue = Number(newValue);
        const newCount = numberValue <= maxCount && numberValue >= minCount ? numberValue : numberValue > maxCount ? maxCount : minCount;
        setUpsellCount(newCount);
    };

    const packagesCartItemCount = (count: number, packagesCartItem: PackagesCartItem) => {
        if (isLoading) return;

        setIsLoading(true);

        dispatch(SellerAction.fetchChangeUpsellPriceAndQuantity(props.reservation.id, packagesCartItem, "quantity", count))
            .then(r => {
                // dispatch(BookingLoginAction.setReservation(r.reservation));
                // dispatch(SellerAction.setReservation(r.reservation));

                toast.success(t("Upsell changed"));
            })
            .finally(() => setIsLoading(false));
    };

    const debouncedChangeHandler = useDebouncedCallback((count, packagesCartItem) => packagesCartItemCount(count, packagesCartItem), 300);

    const onSubmitHandler = () => {
        debouncedChangeHandler(upsellCount, props.packagesCartItem);
    };

    useEffect(() => {
        const count = props.packagesCartItem.drivers_count_adult + props.packagesCartItem.drivers_count_child;
        setUpsellCount(count);
    }, [props.packagesCartItem.drivers_count_adult, props.packagesCartItem.drivers_count_child]);

    return (
        <div className={"flex flex-row items-center justify-center"}>
            <button className={clsx(inputButton, "rounded-tl-md rounded-bl-md")} type={"button"}
                    onClick={() => onCountHandler(-1)}>
                <span>-</span>
            </button>
            <input
                className={"flex border-t border-b w-[52px] leading-tight py-1 px-2 h-[30px]"}
                type="number"
                min={1}
                onChange={e => onChangeCountHandler(e.target.value)}
                value={upsellCount.toString()}
            />
            <button className={clsx(inputButton, "rounded-tr-md rounded-br-md")} type={"button"}
                    onClick={() => onCountHandler(1)}>
                <span>+</span>
            </button>
            <button
                className={clsx(
                    " text-white flex items-center justify-center w-8 h-[30px] font-bold text-xl leading-tight py-1 ml-1 rounded-md",
                    isNotUpdatedValue ? "border-green-400 bg-green-400" : "cursor-not-allowed border-gray-300 bg-gray-300",
                )}
                type={"button"}
                onClick={e => onSubmitHandler()}
            >
                <CheckIcon className={"w-4 h-4 flex-none"} />
            </button>
        </div>
    );
};

export default BookingSellerReservationDetailUpsellQuantity;
